/**
 * Session overview drawer.
 *
 * Focuses on session management:
 * - browse sessions grouped by agent
 * - pin/unpin for quick-switch
 * - rename sessions
 * - archive/remove dashboard sessions from the overview
 * - start a fresh session even when one already exists
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { SessionRow, GatewayAgent, SessionCreateParams } from '../types/sessions';
import { sessionDisplayName } from '../utils/sessionDisplay';
import styles from './SessionPanel.module.css';

interface SessionPanelProps {
  /** Whether the panel is visible */
  isOpen: boolean;
  /** Close the panel */
  onClose: () => void;
  /** Sessions grouped by agent ID */
  sessionsByAgent: Map<string, SessionRow[]>;
  /** Agent definitions */
  agents: GatewayAgent[];
  /** The currently active session key */
  activeSessionKey: string;
  /** Called when user taps a session to switch to it */
  onSessionSelect: (sessionKey: string) => void;
  /** Called to rename a session */
  onSessionRename: (sessionKey: string, label: string | null) => Promise<boolean>;
  /** Called to save spoken aliases for voice/session switching */
  onSessionSpokenAliasesSave: (sessionKey: string, aliases: string[]) => Promise<boolean>;
  /** Called to create a fresh session */
  onSessionCreate: (params: SessionCreateParams) => Promise<string | null>;
  /** Called to hide/remove a session from the overview */
  onSessionArchive: (sessionKey: string) => Promise<boolean>;
  /** Whether a session currently has a local name override */
  hasLocalSessionName: (sessionKey: string) => boolean;
  /** Called to pin or unpin a session from the quick-switch rail */
  onTogglePinned: (sessionKey: string) => void;
  /** Pinned session keys for the active gateway */
  pinnedSessionKeys: Set<string>;
  /** Called to refresh session data */
  onRefresh: () => Promise<void>;
  /** Whether data is loading */
  loading: boolean;
  /** Error message (if any) */
  error?: string | null;
  /** Active gateway name (for display) */
  gatewayName?: string;
  /** Default agent ID */
  defaultAgentId?: string;
}

interface SessionPanelAgentSummary {
  id: string;
  agent: GatewayAgent;
  sessions: SessionRow[];
  displayName: string;
}

interface SessionManageState {
  session: SessionRow;
  agentId: string;
  agentDisplayName: string;
}

function formatRelativeTime(ts: number | null): string {
  if (!ts) return 'No recent activity';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'Updated just now';
  if (diff < 3_600_000) return `Updated ${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `Updated ${Math.floor(diff / 3_600_000)}h ago`;
  return `Updated ${Math.floor(diff / 86_400_000)}d ago`;
}

function parseSessionAgentId(sessionKey: string): string | null {
  const parts = sessionKey.split(':');
  if (parts.length < 2 || parts[0] !== 'agent') return null;
  return parts[1] ?? null;
}

function isMainSessionKey(sessionKey: string): boolean {
  const parts = sessionKey.split(':');
  return parts.length === 3 && parts[0] === 'agent' && parts[2] === 'main';
}

function isSubagentSessionKey(sessionKey: string): boolean {
  return sessionKey.split(':').includes('subagent');
}

function getSubagentDepth(sessionKey: string): number {
  return sessionKey.split(':').filter((part) => part === 'subagent').length;
}

function getSessionKindLabel(session: SessionRow): string {
  if (isSubagentSessionKey(session.key)) return 'Child sub-agent';
  if (isMainSessionKey(session.key)) return 'Main session';
  return 'Session';
}

function getSubagentRelationshipLabel(session: SessionRow, agentDisplayName: string): string | null {
  if (!isSubagentSessionKey(session.key)) return null;

  if (session.parentSessionKey?.trim()) {
    return `Spawned by ${sessionDisplayName({ key: session.parentSessionKey, kind: 'direct', updatedAt: null })}`;
  }

  const depth = getSubagentDepth(session.key);
  return depth > 1 ? `Nested child run · depth ${depth}` : `Child run under ${agentDisplayName}`;
}

function getAgentDisplayName(agent: GatewayAgent): string {
  const preferred = agent.identity?.name || agent.name || agent.id;
  if (/^[a-z0-9_-]+$/i.test(preferred)) {
    return preferred
      .replace(/[-_]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return preferred;
}

function getSessionTitle(session: SessionRow, agentDisplayName?: string): string {
  return sessionDisplayName(session, agentDisplayName);
}

function getSessionPreview(session: SessionRow, title: string): string | null {
  const preview = session.lastMessagePreview?.trim();
  if (!preview) return null;
  if (preview === title) return null;
  return preview;
}

function parseSpokenAliasesInput(value: string): string[] {
  const seen = new Set<string>();
  const aliases: string[] = [];

  for (const part of value.split(',')) {
    const cleaned = part.replace(/\s+/g, ' ').trim();
    if (!cleaned) continue;

    const key = cleaned
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/[^\p{L}\p{N} ]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!key || seen.has(key)) continue;
    seen.add(key);
    aliases.push(cleaned);
  }

  return aliases;
}

function SessionManagePanel({
  session,
  agentId,
  agentDisplayName,
  isPinned,
  hasLocalName,
  onBack,
  onOpen,
  onRename,
  onSaveSpokenAliases,
  onCreate,
  onArchive,
  onTogglePinned,
}: {
  session: SessionRow;
  agentId: string;
  agentDisplayName: string;
  isPinned: boolean;
  hasLocalName: boolean;
  onBack: () => void;
  onOpen: () => void;
  onRename: (sessionKey: string, label: string | null) => Promise<boolean>;
  onSaveSpokenAliases: (sessionKey: string, aliases: string[]) => Promise<boolean>;
  onCreate: (params: SessionCreateParams) => Promise<string | null>;
  onArchive: (sessionKey: string) => Promise<boolean>;
  onTogglePinned: (sessionKey: string) => void;
}) {
  const title = getSessionTitle(session, agentDisplayName);
  const preview = getSessionPreview(session, title);
  const initialName = session.label?.trim() || title;
  const initialSpokenAliases = session.spokenAliases?.join(', ') ?? '';
  const isMainSession = isMainSessionKey(session.key);
  const relationshipLabel = getSubagentRelationshipLabel(session, agentDisplayName);

  const [draftName, setDraftName] = useState(initialName);
  const [draftSpokenAliases, setDraftSpokenAliases] = useState(initialSpokenAliases);
  const [saving, setSaving] = useState(false);
  const [savingAliases, setSavingAliases] = useState(false);
  const [creating, setCreating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [clearingLabel, setClearingLabel] = useState(false);
  const [clearingAliases, setClearingAliases] = useState(false);

  useEffect(() => {
    setDraftName(initialName);
    setDraftSpokenAliases(initialSpokenAliases);
    setConfirmArchive(false);
  }, [initialName, initialSpokenAliases, session.key]);

  const trimmedDraft = draftName.trim();
  const parsedSpokenAliases = parseSpokenAliasesInput(draftSpokenAliases);
  const initialParsedSpokenAliases = parseSpokenAliasesInput(initialSpokenAliases);
  const canSave = trimmedDraft.length > 0 && trimmedDraft !== initialName;
  const canSaveAliases = JSON.stringify(parsedSpokenAliases) !== JSON.stringify(initialParsedSpokenAliases);
  const hasCustomLabel = hasLocalName;
  const hasSpokenAliases = initialParsedSpokenAliases.length > 0;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const ok = await onRename(session.key, trimmedDraft);
      if (ok) {
        onBack();
      }
    } finally {
      setSaving(false);
    }
  }, [canSave, onBack, onRename, session.key, trimmedDraft]);

  const handleClearCustomName = useCallback(async () => {
    if (!hasCustomLabel) return;
    setClearingLabel(true);
    try {
      const ok = await onRename(session.key, null);
      if (ok) {
        onBack();
      }
    } finally {
      setClearingLabel(false);
    }
  }, [hasCustomLabel, onBack, onRename, session.key]);

  const handleSaveSpokenAliases = useCallback(async () => {
    if (!canSaveAliases) return;
    setSavingAliases(true);
    try {
      const ok = await onSaveSpokenAliases(session.key, parsedSpokenAliases);
      if (ok) {
        onBack();
      }
    } finally {
      setSavingAliases(false);
    }
  }, [canSaveAliases, onBack, onSaveSpokenAliases, parsedSpokenAliases, session.key]);

  const handleClearSpokenAliases = useCallback(async () => {
    if (!hasSpokenAliases) return;
    setClearingAliases(true);
    try {
      const ok = await onSaveSpokenAliases(session.key, []);
      if (ok) {
        onBack();
      }
    } finally {
      setClearingAliases(false);
    }
  }, [hasSpokenAliases, onBack, onSaveSpokenAliases, session.key]);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    try {
      const createdKey = await onCreate({ agentId });
      if (createdKey) {
        onBack();
      }
    } finally {
      setCreating(false);
    }
  }, [agentId, onBack, onCreate]);

  const handleArchive = useCallback(async () => {
    if (isMainSession) return;
    if (!confirmArchive) {
      setConfirmArchive(true);
      window.setTimeout(() => setConfirmArchive(false), 3000);
      return;
    }

    setArchiving(true);
    try {
      const ok = await onArchive(session.key);
      if (ok) {
        onBack();
      }
    } finally {
      setArchiving(false);
      setConfirmArchive(false);
    }
  }, [confirmArchive, isMainSession, onArchive, onBack, session.key]);

  return (
    <div className={styles.managePanel}>
      <div className={styles.manageHeader}>
        <div>
          <div className={styles.manageEyebrow}>Manage session</div>
          <div className={styles.manageTitle}>{title}</div>
        </div>
        <button className={styles.manageBackBtn} onClick={onBack}>
          ← Back
        </button>
      </div>

      <div className={styles.manageMetaGrid}>
        <div className={styles.manageMetaCard}>
          <span className={styles.manageMetaLabel}>Agent</span>
          <span className={styles.manageMetaValue}>{agentDisplayName}</span>
        </div>
        <div className={styles.manageMetaCard}>
          <span className={styles.manageMetaLabel}>Type</span>
          <span className={styles.manageMetaValue}>{getSessionKindLabel(session)}</span>
        </div>
        <div className={styles.manageMetaCard}>
          <span className={styles.manageMetaLabel}>Status</span>
          <span className={styles.manageMetaValue}>{formatRelativeTime(session.updatedAt)}</span>
        </div>
      </div>

      {relationshipLabel && (
        <div className={styles.childSessionNotice}>
          <span aria-hidden="true">↳</span>
          <span>{relationshipLabel}</span>
        </div>
      )}

      {preview && (
        <div className={styles.previewCard}>
          <div className={styles.previewLabel}>Latest preview</div>
          <div className={styles.previewText}>{preview}</div>
        </div>
      )}

      <div className={styles.manageSection}>
        <label className={styles.manageLabel} htmlFor="session-name-input">Session name</label>
        <input
          id="session-name-input"
          className={styles.manageInput}
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          placeholder="Give this session a real name"
          maxLength={80}
        />
        <div className={styles.manageHint}>
          Rename the session on this device so it is easy to spot later.
        </div>
        <div className={styles.inlineActions}>
          <button
            className={styles.primaryBtn}
            onClick={() => void handleSave()}
            disabled={!canSave || saving}
          >
            {saving ? 'Saving…' : 'Save name'}
          </button>
          {hasCustomLabel && (
            <button
              className={styles.secondaryBtn}
              onClick={() => void handleClearCustomName()}
              disabled={clearingLabel}
            >
              {clearingLabel ? 'Clearing…' : 'Remove local rename'}
            </button>
          )}
        </div>
      </div>

      <div className={styles.manageSection}>
        <label className={styles.manageLabel} htmlFor="session-spoken-aliases-input">Spoken aliases</label>
        <input
          id="session-spoken-aliases-input"
          className={styles.manageInput}
          value={draftSpokenAliases}
          onChange={(e) => setDraftSpokenAliases(e.target.value)}
          placeholder="Sarah, personal lane"
          maxLength={160}
        />
        <div className={styles.manageHint}>
          Used only for voice/session switching. Comma-separated aliases here do not change the visible session name.
        </div>
        <div className={styles.inlineActions}>
          <button
            className={styles.primaryBtn}
            onClick={() => void handleSaveSpokenAliases()}
            disabled={!canSaveAliases || savingAliases}
          >
            {savingAliases ? 'Saving…' : 'Save aliases'}
          </button>
          {hasSpokenAliases && (
            <button
              className={styles.secondaryBtn}
              onClick={() => void handleClearSpokenAliases()}
              disabled={clearingAliases}
            >
              {clearingAliases ? 'Clearing…' : 'Clear aliases'}
            </button>
          )}
        </div>
      </div>

      <div className={styles.manageSection}>
        <div className={styles.manageLabel}>Session actions</div>
        <div className={styles.inlineActions}>
          <button className={styles.primaryBtn} onClick={onOpen}>
            Open session
          </button>
          <button
            className={styles.secondaryBtn}
            onClick={() => onTogglePinned(session.key)}
          >
            {isPinned ? 'Unpin from quick switch' : 'Pin to quick switch'}
          </button>
          <button
            className={styles.secondaryBtn}
            onClick={() => void handleCreate()}
            disabled={creating}
          >
            {creating ? 'Starting…' : 'Start new session'}
          </button>
        </div>
      </div>

      <div className={`${styles.manageSection} ${styles.dangerSection}`}>
        <div className={styles.manageLabel}>Remove from overview</div>
        <div className={styles.manageHint}>
          {isMainSession
            ? 'The main session stays in the overview. Start a new session instead.'
            : 'This hides the session from the overview on this device without deleting the underlying conversation.'}
        </div>
        <button
          className={styles.dangerBtn}
          onClick={() => void handleArchive()}
          disabled={isMainSession || archiving}
        >
          {isMainSession
            ? 'Main session cannot be removed'
            : confirmArchive
              ? 'Confirm remove'
              : archiving
                ? 'Removing…'
                : 'Remove from overview'}
        </button>
      </div>
    </div>
  );
}

function SessionItem({
  session,
  agentDisplayName,
  isActive,
  isPinned,
  onSelect,
  onManage,
  onTogglePinned,
}: {
  session: SessionRow;
  agentDisplayName: string;
  isActive: boolean;
  isPinned: boolean;
  onSelect: () => void;
  onManage: () => void;
  onTogglePinned: () => void;
}) {
  const title = getSessionTitle(session, agentDisplayName);
  const preview = getSessionPreview(session, title);
  const isSubagentSession = isSubagentSessionKey(session.key);
  const relationshipLabel = getSubagentRelationshipLabel(session, agentDisplayName);

  return (
    <div
      className={`${styles.sessionItem} ${isActive ? styles.active : ''} ${isSubagentSession ? styles.childSessionItem : ''}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label={`${title}${isSubagentSession ? ', child sub-agent session' : ''}`}
      onKeyDown={(e) => e.key === 'Enter' && onSelect()}
    >
      {isSubagentSession && (
        <div className={styles.childSessionRail} aria-hidden="true">↳</div>
      )}
      <div className={styles.sessionInfo}>
        <div className={`${styles.sessionName} ${isActive ? styles.activeText : ''}`}>
          {title}
        </div>
        <div className={styles.sessionMeta}>
          {isActive && <span className={styles.sessionBadge}>Active</span>}
          {isPinned && <span className={styles.sessionPinned}>Pinned</span>}
          {isSubagentSession && <span className={styles.childSessionBadge}>Sub-agent child</span>}
          <span className={styles.sessionTime}>{formatRelativeTime(session.updatedAt)}</span>
        </div>
        {relationshipLabel && (
          <div className={styles.childSessionRelationship}>{relationshipLabel}</div>
        )}
        {preview && (
          <div className={styles.sessionPreview}>{preview}</div>
        )}
      </div>
      <div className={styles.sessionActions}>
        <button
          className={`${styles.pinBtn} ${isPinned ? styles.pinBtnActive : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePinned();
          }}
          title={isPinned ? 'Unpin from quick switch' : 'Pin to quick switch'}
          aria-label={isPinned ? 'Unpin session from quick switch' : 'Pin session to quick switch'}
          aria-pressed={isPinned}
        >
          {isPinned ? '★' : '☆'}
        </button>
        <button
          className={styles.manageBtn}
          onClick={(e) => {
            e.stopPropagation();
            onManage();
          }}
          title="Manage session"
          aria-label="Manage session"
        >
          ⋯
        </button>
      </div>
    </div>
  );
}

function AgentRow({
  agent,
  displayName,
  isDefault,
  sessionCount,
  isExpanded,
  onToggle,
  onCreateSession,
  creating,
}: {
  agent: GatewayAgent;
  displayName: string;
  isDefault: boolean;
  sessionCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  onCreateSession: () => void;
  creating: boolean;
}) {
  const emoji = agent.identity?.emoji;

  return (
    <div className={styles.agentRow}>
      <div
        className={styles.agentRowHeader}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        <div className={styles.agentRowLeft}>
          {emoji && <span className={styles.agentEmoji}>{emoji}</span>}
          <span className={styles.agentName}>{displayName}</span>
          {isDefault && <span className={styles.defaultBadge}>default</span>}
          <span className={styles.sessionCount}>
            {sessionCount} {sessionCount === 1 ? 'session' : 'sessions'}
          </span>
        </div>
        <div className={styles.agentRowRight}>
          <button
            className={styles.newSessionBtn}
            onClick={(e) => {
              e.stopPropagation();
              onCreateSession();
            }}
            disabled={creating}
            aria-label={`Start new ${displayName} session`}
          >
            {creating ? 'Starting…' : '+ New'}
          </button>
          <span className={`${styles.expandArrow} ${isExpanded ? styles.expandArrowOpen : ''}`}>
            ▾
          </span>
        </div>
      </div>
    </div>
  );
}

export function SessionPanel({
  isOpen,
  onClose,
  sessionsByAgent,
  agents,
  activeSessionKey,
  onSessionSelect,
  onSessionRename,
  onSessionSpokenAliasesSave,
  onSessionCreate,
  onSessionArchive,
  hasLocalSessionName,
  onTogglePinned,
  pinnedSessionKeys,
  onRefresh,
  loading,
  error,
  gatewayName,
  defaultAgentId = 'main',
}: SessionPanelProps) {
  const [manageSession, setManageSession] = useState<SessionManageState | null>(null);
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());
  const [creatingAgents, setCreatingAgents] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) return;
    const activeAgentId = parseSessionAgentId(activeSessionKey);
    if (!activeAgentId) return;
    setExpandedAgents((prev) => {
      if (prev.has(activeAgentId)) return prev;
      const next = new Set(prev);
      next.add(activeAgentId);
      return next;
    });
  }, [isOpen, activeSessionKey]);

  useEffect(() => {
    if (!isOpen) setManageSession(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (manageSession) {
          setManageSession(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, manageSession, onClose]);

  const handleRefresh = useCallback(async () => {
    await onRefresh();
  }, [onRefresh]);

  const toggleAgent = useCallback((agentId: string) => {
    setExpandedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }
      return next;
    });
  }, []);

  const handleCreateSession = useCallback(async (params: SessionCreateParams): Promise<string | null> => {
    const agentId = params.agentId ?? defaultAgentId;
    if (!agentId) return null;

    setCreatingAgents((prev) => new Set(prev).add(agentId));
    try {
      const createdKey = await onSessionCreate({ ...params, agentId });
      if (createdKey) {
        onClose();
      }
      return createdKey;
    } finally {
      setCreatingAgents((prev) => {
        const next = new Set(prev);
        next.delete(agentId);
        return next;
      });
    }
  }, [defaultAgentId, onClose, onSessionCreate]);

  const sortedAgents = useMemo<SessionPanelAgentSummary[]>(() => {
    const agentMap = new Map(agents.map((agent) => [agent.id, agent]));
    const allIds = new Set([
      ...agents.map((agent) => agent.id),
      ...sessionsByAgent.keys(),
    ]);

    return Array.from(allIds)
      .map((id) => {
        const agent = agentMap.get(id) ?? { id, name: id };
        return {
          id,
          agent,
          sessions: sessionsByAgent.get(id) ?? [],
          displayName: getAgentDisplayName(agent),
        };
      })
      .sort((a, b) => {
        if (a.id === defaultAgentId) return -1;
        if (b.id === defaultAgentId) return 1;
        return a.displayName.localeCompare(b.displayName);
      });
  }, [agents, sessionsByAgent, defaultAgentId]);

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.panel}>
        <div className={styles.handle} />

        <div className={styles.header}>
          <div>
            <div className={styles.title}>
              {gatewayName ? `Session overview — ${gatewayName}` : 'Session overview'}
            </div>
            <div className={styles.subtitle}>
              Rename, pin, archive, and start fresh sessions.
            </div>
          </div>
          <div className={styles.headerActions}>
            <button
              className={`${styles.refreshBtn} ${loading ? styles.spinning : ''}`}
              onClick={() => void handleRefresh()}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              🔄
            </button>
            <button
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className={styles.content}>
          {loading && sessionsByAgent.size === 0 && (
            <div className={styles.loading}>Loading sessions…</div>
          )}

          {error && (
            <div className={styles.error}>{error}</div>
          )}

          {manageSession && (
            <SessionManagePanel
              session={manageSession.session}
              agentId={manageSession.agentId}
              agentDisplayName={manageSession.agentDisplayName}
              isPinned={pinnedSessionKeys.has(manageSession.session.key)}
              hasLocalName={hasLocalSessionName(manageSession.session.key)}
              onBack={() => setManageSession(null)}
              onOpen={() => {
                onSessionSelect(manageSession.session.key);
                onClose();
              }}
              onRename={onSessionRename}
              onSaveSpokenAliases={onSessionSpokenAliasesSave}
              onCreate={handleCreateSession}
              onArchive={onSessionArchive}
              onTogglePinned={onTogglePinned}
            />
          )}

          {!manageSession && !loading && sortedAgents.length === 0 && !error && (
            <div className={styles.empty}>No agents found</div>
          )}

          {!manageSession && sortedAgents.map(({ id, agent, sessions, displayName }) => {
            const isExpanded = expandedAgents.has(id);
            const isDefault = id === defaultAgentId;
            const orderedSessions = [...sessions].sort((a, b) => {
              const aPinned = pinnedSessionKeys.has(a.key);
              const bPinned = pinnedSessionKeys.has(b.key);
              if (aPinned && !bPinned) return -1;
              if (!aPinned && bPinned) return 1;
              if (a.key === activeSessionKey) return -1;
              if (b.key === activeSessionKey) return 1;
              return (b.updatedAt ?? 0) - (a.updatedAt ?? 0);
            });

            return (
              <div key={id} className={styles.agentGroup}>
                <AgentRow
                  agent={agent}
                  displayName={displayName}
                  isDefault={isDefault}
                  sessionCount={sessions.length}
                  isExpanded={isExpanded}
                  onToggle={() => toggleAgent(id)}
                  onCreateSession={() => void handleCreateSession({ agentId: id })}
                  creating={creatingAgents.has(id)}
                />

                {isExpanded && orderedSessions.length > 0 && (
                  <div className={styles.sessionsNested}>
                    {orderedSessions.map((session) => (
                      <SessionItem
                        key={session.key}
                        session={session}
                        agentDisplayName={displayName}
                        isActive={session.key === activeSessionKey}
                        isPinned={pinnedSessionKeys.has(session.key)}
                        onSelect={() => {
                          onSessionSelect(session.key);
                          onClose();
                        }}
                        onManage={() => setManageSession({
                          session,
                          agentId: id,
                          agentDisplayName: displayName,
                        })}
                        onTogglePinned={() => onTogglePinned(session.key)}
                      />
                    ))}
                  </div>
                )}

                {isExpanded && orderedSessions.length === 0 && (
                  <div className={styles.emptyAgentState}>
                    <div className={styles.emptyAgentCopy}>No saved sessions yet.</div>
                    <div className={styles.inlineActions}>
                      <button
                        className={styles.secondaryBtn}
                        onClick={() => void handleCreateSession({ agentId: id, key: 'main' })}
                        disabled={creatingAgents.has(id)}
                      >
                        Open main session
                      </button>
                      <button
                        className={styles.primaryBtn}
                        onClick={() => void handleCreateSession({ agentId: id })}
                        disabled={creatingAgents.has(id)}
                      >
                        {creatingAgents.has(id) ? 'Starting…' : 'Start new session'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
